import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { DsButton, ModalDialog } from './ds';
import { TOAST_SURFACE } from './ds/toast';
import {
  ENV_FILE_MAX_BYTES,
  ENV_IMPORT_MAX_VARIABLES,
  EnvPair,
  looksLikeDotenv,
  planEnvImport,
} from '../utils/envImport';

const TOO_LONG = `That is more than ${Math.round(ENV_FILE_MAX_BYTES / 1024)} KB, so it was not read.`;

/**
 * Filling the variables list from a `.env`: the box it is pasted into, what
 * it would do, and the button that says yes.
 *
 * The text is read here, in the browser, and goes nowhere: what the dialog
 * hands back is a list of names and values for the form to hold, and the form
 * saves them the way it saves a variable typed by hand — on its own Save.
 *
 * Two things about what is on screen:
 *
 *   What an import would do is said in counts. A value is never drawn outside
 *   the box it was pasted into, so the answer to "what will this change" does
 *   not put a page of secrets on the screen.
 *
 *   The body is closed to all three session recorders. Their own rules cover
 *   password fields, and the variable rows of the forms are those; a text box
 *   holding a whole `.env` is not, and would be recorded in full. `sentry-block`
 *   / `data-sentry-block` is Sentry Replay's, `amp-block` Amplitude's,
 *   `data-private` and `_lr-hide` LogRocket's.
 */
export const EnvPasteDialog = ({ open, title = 'Paste .env', initialText = '', existing, reservedKeys, onClose, onImport }: {
  open: boolean,
  /** "Upload .env" when the text came from a file. */
  title?: string,
  /** What a file held, or what was pasted into a name field. */
  initialText?: string,
  /** The list as the form holds it now, unsaved edits included. */
  existing: EnvPair[],
  /** Names the platform fills in itself, which a file never writes over. */
  reservedKeys?: string[],
  onClose: () => void,
  /** The pairs to add or replace. Nothing is removed by an import. */
  onImport: (pairs: EnvPair[]) => void,
}) => {
  const [text, setText] = useState(initialText);

  // Each opening starts from what opened it: a file, a paste, or nothing.
  useEffect(() => {
    if (open) {
      setText(initialText);
    }
  }, [open, initialText]);

  // The ceiling a chosen file is held to, held to a paste as well: past it
  // nothing is parsed, and the dialog says why.
  const isTooLong = text.length > ENV_FILE_MAX_BYTES;

  const plan = useMemo(
    () => planEnvImport(isTooLong ? '' : text, existing, reservedKeys),
    [text, isTooLong, existing, reservedKeys]
  );

  const incoming = [...plan.added, ...plan.replaced];
  const hasText = !isTooLong && !!text.trim();
  const skippedCount = plan.skipped.length + plan.unreadableLines + plan.overLimit;
  const nothingFound = hasText && !incoming.length && !plan.unchanged.length && !skippedCount;

  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  // The whole preview: one quiet line beside the buttons.
  const summary = nothingFound
    ? 'No KEY=value lines found.'
    : hasText
      ? [
        plan.added.length ? `${plan.added.length} new` : '',
        plan.replaced.length ? `${plan.replaced.length} updated` : '',
        plan.unchanged.length ? `${plan.unchanged.length} already set` : '',
        skippedCount ? `${skippedCount} skipped` : '',
      ].filter(Boolean).join(' · ')
      : '';

  // On the body, not where the button is: the dialog is opened from inside
  // cards and forms, and `Modal` places itself with `position: fixed` — which
  // is measured from the nearest transformed ancestor when there is one.
  return createPortal(
    <ModalDialog
      open={open}
      onClose={onClose}
      title={title}
      blurb="One KEY=value per line. Existing keys are updated."
      width="34rem"
      closeOnClickOutside={false}
      footer={
        <>
          <span data-testid="env-import-summary" className="mr-auto self-center min-w-0 text-ds-125 text-navy-200">
            {summary}
          </span>
          <DsButton onClick={onClose}>Cancel</DsButton>
          <DsButton
            variant="action"
            isDisabled={!incoming.length}
            onClick={() => { onImport(incoming); onClose(); }}
          >
            Add to list
          </DsButton>
        </>
      }
    >
      <div
        data-sentry-block
        data-private
        data-testid="env-import-private"
        className="sentry-block amp-block _lr-hide flex flex-col gap-3"
      >
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={'API_KEY=…\nREDIS_URL=…'}
          rows={9}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          aria-label="Contents of the .env file"
          className="b4-thin block w-full resize-y rounded-control border border-navy-500 bg-navy-950 px-3 py-2.5 font-courrier text-[16px] lg:text-ds-125 leading-[1.7] text-white placeholder:text-navy-300 outline-none focus:border-action focus:shadow-[0_0_0_3px_rgba(32,138,236,0.25)]"
        />

        {isTooLong ? (
          <p role="alert" className="m-0 text-ds-125 leading-[1.5] text-state-err">{TOO_LONG}</p>
        ) : null}

        {/* The skips that are ours rather than the file's get a sentence: the
            file asked for something and it was not done, on purpose, and a
            count alone would leave the reader wondering why. */}
        {plan.reserved.length ? (
          <p className="m-0 text-ds-125 leading-[1.5] text-state-warn">
            {plan.reserved.join(' and ')} {plan.reserved.length === 1 ? 'is' : 'are'} set
            by Back4app and {plan.reserved.length === 1 ? 'was' : 'were'} left as {plan.reserved.length === 1 ? 'it is' : 'they are'}.
          </p>
        ) : null}

        {plan.overLimit ? (
          <p className="m-0 text-ds-125 leading-[1.5] text-state-warn">
            {count(plan.overLimit, 'variable was', 'variables were')} left out: an import
            stops at {ENV_IMPORT_MAX_VARIABLES} variables. You can still add them one by one.
          </p>
        ) : null}

        {/* The one thing the reader has to check by eye: readers of .env
            files disagree on whether this `#` starts a comment. */}
        {plan.bareHash.length ? (
          <p className="m-0 text-ds-125 leading-[1.5] text-state-warn">
            {plan.bareHash.join(', ')}: the value has a # in it, which was kept. If
            that was meant as a comment, remove it from the value.
          </p>
        ) : null}
      </div>
    </ModalDialog>,
    document.body
  );
};

/**
 * The two ways in — a file and a paste — for a screen that holds a variables
 * list. Both end in the dialog above, so a file is read back as counts before
 * it changes anything, the same as a paste.
 *
 * `element` goes anywhere in the screen's tree: it is the hidden file input
 * and the dialog, which puts itself on the body.
 */
export const useEnvImport = ({ existing, reservedKeys, onImport }: {
  existing: EnvPair[],
  reservedKeys?: string[],
  onImport: (pairs: EnvPair[]) => void,
}) => {
  const [draft, setDraft] = useState<{ text: string, fromFile: boolean } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refuse = (message: string) => toast.error(message, { className: TOAST_SURFACE });

  const onFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // So that choosing the same file again is still a change.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (file.size > ENV_FILE_MAX_BYTES) {
      refuse(TOO_LONG);
      return;
    }

    try {
      const content = await file.text();

      // A NUL byte is the cheapest sign of something that is not text.
      if (content.includes('\u0000')) {
        refuse('That does not look like a text file, so it was not read.');
        return;
      }

      setDraft({ text: content, fromFile: true });
    } catch (e) {
      refuse('That file could not be read.');
    }
  };

  /** Opens the dialog, on `text` when there is some. */
  const paste = (text = '') => setDraft({ text, fromFile: false });

  /** Opens the file picker; the dialog follows with what the file holds. */
  const upload = () => fileRef.current?.click();

  /**
   * For a variable's name field. A `.env` pasted where a name goes is a
   * request to import it: the paste is stopped and the dialog opens on it, so
   * its lines are read as variables instead of landing in one field as a name
   * nothing would accept. Returns whether it took the paste.
   */
  const takeNamePaste = (event: React.ClipboardEvent<HTMLInputElement>): boolean => {
    const pasted = event.clipboardData.getData('text');

    if (!looksLikeDotenv(pasted)) {
      return false;
    }

    event.preventDefault();
    paste(pasted);

    return true;
  };

  const element = (
    <>
      <input ref={fileRef} type="file" className="hidden" onChange={onFileChosen} aria-label="Choose a .env file" />
      <EnvPasteDialog
        open={!!draft}
        title={draft?.fromFile ? 'Upload .env' : 'Paste .env'}
        initialText={draft?.text || ''}
        existing={existing}
        reservedKeys={reservedKeys}
        onClose={() => setDraft(null)}
        onImport={onImport}
      />
    </>
  );

  return { paste, upload, takeNamePaste, element };
};
